import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { ButtonBack } from './styles';
import ResourceSelector from '../../../../containers/ResourceSelector';
import MainView from './MainView';
import { updateEditableMedia } from '../../../../containers/Editor/actions';
import { getYoutubeLink } from '../../../../containers/Editor/utils/helpers';
import { EditableType } from '../../../../containers/Editor/constants';
import { getOptimizedImage, getImageCenteredPositionAndSize } from '../../../../containers/Editor/utils/helpers';
import Utils from '../../../../utils';
import { Api } from '../../../../typings/api';
import { StyleColor } from '../../../../utils/styles-constants';
import { removeFileFromImport } from '../../../../containers/ImportManager/actions';

interface MediaPanelProps {
	type: EditableType;
	id: string;
	page: number;
	label: string;
	defaultLink: string;
	driveLink: string;
	externalLink: string;
	isExternalLinkActive: boolean;
	frameWidth: number;
	frameHeight: number;
	importManagerId: string;
	isImporting: boolean;
	dispatch: Dispatch<StoreState>;
	isPercentageValues: boolean;
}

interface ConfigItem {
	iconName: string;
	defaultLabel: string;
	isValidResourceFromApp: (item: Api.DriveItem) => boolean;
	fileExtensions: string[];
	hasExternalLink: boolean;
	canDragAndResize: boolean;
}

const config: { [k: string]: ConfigItem } = {
	[EditableType.image]: {
		iconName: 'iconset_uprez_image',
		defaultLabel: 'messages.default_image',
		isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Image,
		fileExtensions: ['.jpg', '.jpeg', '.png', '.svg'],
		hasExternalLink: false,
		canDragAndResize: true,
	},
	[EditableType.video]: {
		iconName: 'iconset_uprez_video',
		defaultLabel: 'messages.default_video',
		isValidResourceFromApp: ({ type }) => type === Api.DriveItemType.Movie,
		fileExtensions: ['.mp4', '.webm', '.avi', '.mov'],
		hasExternalLink: true,
		canDragAndResize: false,
	},
};

const IMAGE_MAX_WIDTH = 1920;
const SKIP_OPTIMIZATION_IMAGE_FORMAT = 'png';

let isMounted = false;

const MediaPanel = ({
	type,
	id,
	page,
	label,
	defaultLink,
	driveLink,
	externalLink,
	isExternalLinkActive,
	frameWidth,
	frameHeight,
	importManagerId,
	isImporting,
	dispatch,
	isPercentageValues,
}: MediaPanelProps) => {
	const {
		iconName,
		defaultLabel,
		isValidResourceFromApp,
		fileExtensions,
		hasExternalLink,
		canDragAndResize,
	} = config[type];

	const [isResourceSelectorDisplayed, setIsResourceSelectorDisplayed] = React.useState(isImporting);

	const handleResourceSelect = (driveItem: Api.DriveItem): void => {
		const { src, label, name, resolution, status } = driveItem;
		let newDriveLink = src;
		let width = frameWidth;
		let height = frameHeight;

		if (resolution) {
			width = +resolution.split('x')[0];
			height = +resolution.split('x')[1];
		}

		const canOptimizeImage = type === EditableType.image &&
			width > IMAGE_MAX_WIDTH &&
			Utils.getFileExtension(name) !== SKIP_OPTIMIZATION_IMAGE_FORMAT;

		if (canOptimizeImage) {
			const optimizedImage = getOptimizedImage(driveItem, width, height, IMAGE_MAX_WIDTH);
			width = optimizedImage.width;
			height = optimizedImage.height;
			newDriveLink = optimizedImage.src;
		}

		const isSameImage = type === EditableType.image && driveLink === newDriveLink;
		if (isSameImage) {
			// we have to add some hash to url in order to make browser to load the same file
			newDriveLink += `?${Math.random()}&`;
		}

		dispatch(updateEditableMedia(page, id, {
			...getImageCenteredPositionAndSize(frameWidth, frameHeight, width, height, isPercentageValues),
			driveLink: newDriveLink,
			label,
			isExternalLinkActive: false,
			isLoaded: false,
			canDragAndResize,
			isEditedFromBackoffice: true,
		}));

		const isProcessing = status && status !== Api.AssetItemStatus.LIVE;

		// do not update state on unmounted component to avoid memory leaks
		if (isMounted && !isProcessing) {
			setIsResourceSelectorDisplayed(false);
		}
	};

	const handleExternalLinkChange = (value: string): void => {
		dispatch(updateEditableMedia(page, id, {
			externalLink: getYoutubeLink(value),
			isExternalLinkActive: true,
			canDragAndResize,
			isEditedFromBackoffice: true,
		}));
	};

	const handleResourceRemove = (): void => {
		if (isImporting) {
			dispatch(removeFileFromImport(importManagerId));
		}

		dispatch(updateEditableMedia(page, id, {
			label: '',
			driveLink: '',
			externalLink: getYoutubeLink(defaultLink) ? defaultLink : '',
			isExternalLinkActive: !!getYoutubeLink(defaultLink),
			canDragAndResize: false,
			isEditedFromBackoffice: true,
		}));
	};

	React.useEffect(() => {
		isMounted = true;
		return () => {
			isMounted = false;
		};
	}, []);

	React.useEffect(() => {
		setIsResourceSelectorDisplayed(isImporting);
	}, [isImporting]);

	const isResetButtonDisplayed = isExternalLinkActive
		? !!externalLink && externalLink !== defaultLink
		: !!driveLink;

	return (
		<>
			{!isResourceSelectorDisplayed
				? (
					<MainView
						iconName={iconName}
						defaultLabel={defaultLabel}
						label={driveLink ? label : ''}
						externalLink={externalLink}
						isExternalLinkActive={isExternalLinkActive}
						isResetButtonDisplayed={isResetButtonDisplayed}
						onReplaceClick={() => setIsResourceSelectorDisplayed(true)}
						onResetClick={handleResourceRemove}
						onExternalLinkChange={handleExternalLinkChange}
					/>
				)
				: (
					<>
						<ButtonBack
							type="button"
							onClick={() => setIsResourceSelectorDisplayed(false)}
						>
							{Utils.getSvgIcon('iconset_back')}
							<Translate value="messages.back" />
						</ButtonBack>
						<ResourceSelector
							id={importManagerId}
							label="messages.replace_file"
							isValidResourceFromApp={isValidResourceFromApp}
							isOptimizedImageImport={type === EditableType.image}
							fileExtensions={fileExtensions}
							isFileExtensionsDisplayed={true}
							hasExternalLink={hasExternalLink}
							externalLink={externalLink}
							onSelect={handleResourceSelect}
							onExternalLinkChange={handleExternalLinkChange}
							actionButtonColor={StyleColor.Blue600}
						/>
					</>
				)
			}
		</>
	);
};

export default MediaPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/MediaPanel/index.tsx